import { useMemo } from 'react';
import { AgeChart as NewAgeChart } from '~/components/TalentCharts/Age';
import { CountryChart as NewCountryChart } from '~/components/TalentCharts/Country';
import { GenderChart as NewGenderChart } from '~/components/TalentCharts/Gender';
import { GrowthRateChart } from '~/components/TalentCharts/GrowthRateChart/GrowthRateChart';
import { ChartTheme } from '~/components/TalentCharts/PlatformCharts';
import { FEATURE_FLAGS, SOCIAL_PLATFORMS } from '~/constants';
import { SupportedCharts } from '~/constants/platforms';
import { useFetchCreatorPublic } from '~/hooks/api/media-kits/useFetchCreatorPublic';
import { useGetCreatorMetricsTimeline } from '~/hooks/api/useGetCreatorMetricsTimeline';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { getShareTokenByCreatorSlug } from '~/lib/getShareToken';
import { getInsightId } from '~/screens/CreatorAudienceDemographics';
import { useInsights } from '~/screens/CreatorAudienceDemographics/GenderDistribution';

import { AgeChart as OldAgeChart } from './AgeChart';
import { CountryChart as OldCountryChart } from './CountryChart';
import { GenderChart as OldGenderChart } from './GenderChart';

interface Props {
  platform: SOCIAL_PLATFORMS;
  demographics?: SupportedCharts[];
  creatorSlug: string;
}

// Media kit custom theme using CSS variables with blue defaults
const mediaKitTheme: ChartTheme = {
  gender: {
    primary: 'var(--media-kit-chart-background-primary, #155FEF)',
    secondary: 'var(--media-kit-chart-background-secondary, #80A9F9)',
    tertiary: 'var(--media-kit-chart-background-tertiary, #CCDDFF)',
    background: 'var(--media-kit-background-primary, #ffffff)',
    backgroundAlpha: 'var(--media-kit-background-alpha, #dee2e8)',
    textBase: 'var(--media-kit-text-base, #15191e)',
    textSubtle: 'var(--media-kit-text-subtle, #303d4f)',
  },
  age: {
    primary: 'var(--media-kit-chart-background-primary, #155FEF)',
    background: 'var(--media-kit-background-primary, #ffffff)',
    backgroundAlpha: 'var(--media-kit-background-alpha, #dee2e8)',
    textBase: 'var(--media-kit-text-base, #15191e)',
  },
  country: {
    primary: 'var(--media-kit-chart-background-primary, #155FEF)',
    background: 'var(--media-kit-background-primary, #ffffff)',
    backgroundAlpha: 'var(--media-kit-background-alpha, #dee2e8)',
    textBase: 'var(--media-kit-text-base, #15191e)',
  },
  growthRate: {
    primary: 'var(--media-kit-chart-background-primary, #155FEF)',
    secondary: 'var(--media-kit-chart-background-secondary, #80A9F9)',
    background: 'var(--media-kit-background-primary, #ffffff)',
    backgroundAlpha: 'var(--media-kit-background-alpha, #dee2e8)',
    textBase: 'var(--media-kit-text-base, #15191e)',
  },
};

export const AllCharts = ({ platform, demographics, creatorSlug }: Props) => {
  const { isEnabled: isNewChartsEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsGrowthRateDesigns,
  );

  const creator = useFetchCreatorPublic(creatorSlug);

  const insightId =
    getInsightId({
      creator: creator.data,
      platform,
    }) || '';

  const insights = useInsights({ insightId, platform });

  const metricsTimeline = useGetCreatorMetricsTimeline({
    creatorId: creator.data?.id,
    platform,
    shareToken: getShareTokenByCreatorSlug(creatorSlug ?? ''),
  });

  const genders = insights?.data?.genders;
  const ages = insights?.data?.ages;
  const countries = insights?.data?.countries;

  // Transform genders for old chart format
  const oldGenderData = useMemo(() => {
    if (!genders) return null;
    const male = genders.find((g) => g.label.toLowerCase() === 'male')?.value;
    const female = genders.find((g) => g.label.toLowerCase() === 'female')
      ?.value;
    const other = genders.find(
      (g) =>
        g.label.toLowerCase() === 'other' ||
        g.label.toLowerCase() === 'unknown',
    )?.value;
    return {
      male: male ? Number((male * 100).toFixed(1)) : undefined,
      female: female ? Number((female * 100).toFixed(1)) : undefined,
      other: other ? Number((other * 100).toFixed(1)) : undefined,
    };
  }, [genders]);

  // Transform ages for old chart format
  const oldAgeData = useMemo(() => {
    if (!ages) return [];
    return ages.map((age) => ({
      age: age.label,
      count: Number((age.value * 100).toFixed(1)),
    }));
  }, [ages]);

  // Transform countries for old chart format
  const oldCountryData = useMemo(() => {
    if (!countries) return [];
    return countries
      .sort((c1, c2) => c2.value - c1.value)
      .slice(0, 3)
      .map((country) => ({
        country: country.meta?.countryName || country.label || 'Others',
        value: Number((country.value * 100).toFixed(1)),
      }));
  }, [countries]);

  const isEmptyOrUndefined = (data: unknown[] | undefined | null) =>
    data === undefined || data === null || data.length === 0;

  return (
    <section>
      <div className="relative">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(338px,1fr))] items-stretch gap-6">
          {demographics?.includes('gender') &&
            !isEmptyOrUndefined(genders) &&
            (isNewChartsEnabled ? (
              <NewGenderChart genders={genders} theme={mediaKitTheme.gender} />
            ) : (
              <OldGenderChart
                male={oldGenderData?.male}
                female={oldGenderData?.female}
                other={oldGenderData?.other}
              />
            ))}

          {demographics?.includes('age') &&
            !isEmptyOrUndefined(ages) &&
            (isNewChartsEnabled ? (
              <NewAgeChart ages={ages} theme={mediaKitTheme.age} />
            ) : (
              <OldAgeChart ages={oldAgeData} />
            ))}

          {demographics?.includes('country') &&
            !isEmptyOrUndefined(countries) &&
            (isNewChartsEnabled ? (
              <NewCountryChart
                countries={countries}
                theme={mediaKitTheme.country}
              />
            ) : (
              <OldCountryChart countries={oldCountryData} />
            ))}

          {demographics?.includes('growthRate') && metricsTimeline.data && (
            <div className="col-span-full">
              <GrowthRateChart
                platform={platform}
                metricsTimeline={metricsTimeline.data}
                theme={mediaKitTheme.growthRate}
                containerClassName="w-full"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
